export type ResourceKind = 'http' | 'mcp' | 'connector';
export type Health = 'HEALTHY' | 'DEGRADED' | 'UNHEALTHY' | 'UNKNOWN' | 'STALE' | 'UNREACHABLE' | 'ACCESS_REQUIRED';
export type EvidenceState = 'OBSERVED' | 'CONFIGURED' | 'INFERRED' | 'UNKNOWN';

export interface Actor { id: string; email: string | null }
export interface Workspace {
  id: string;
  name: string;
  mode: string;
  capabilities: string[];
}
export interface Resource {
  id: string;
  name: string;
  kind: ResourceKind;
  endpoint: string;
  status: string;
  lastSeen: string | null;
  health: Health;
  evidenceState: EvidenceState;
}
export interface TopologyNode {
  id: string;
  resourceId: string;
  kind: string;
  label: string;
  status: string;
  source: string | null;
  observedAt: string | null;
  evidenceState: EvidenceState;
}
export interface TopologyEdge {
  id: string;
  fromId: string;
  toId: string;
  kind: string;
  source: string | null;
  observedAt: string | null;
  evidenceState: EvidenceState;
}
export interface Topology { nodes: TopologyNode[]; edges: TopologyEdge[] }
export interface Diagnostic {
  id: string;
  resourceId: string;
  type: ResourceKind;
  status: Health;
  startedAt: string;
  finishedAt: string;
  latencyMs?: number;
  httpStatus?: number;
  detail: string;
  evidenceId?: string;
}
export interface Operation {
  id: string;
  resourceId: string;
  action: string;
  status: string;
  policyDecision?: string;
  policyReason?: string;
  reason?: string;
  detail?: string;
  startedAt?: string;
  finishedAt?: string;
  verification?: unknown;
}
export interface AuditEvent {
  id: string;
  at: string;
  actorId: string;
  action: string;
  resourceId: string;
  outcome: string;
  detail: string;
}
export interface Analysis {
  summary: string;
  findings: string[];
  evidenceIds: string[];
  provider: string;
  model: string;
}

export class ApiError extends Error {
  constructor(message: string, public readonly status: number) {
    super(message);
    this.name = 'ApiError';
  }
}

const configuredBase = import.meta.env.VITE_API_BASE_URL?.trim();
const apiBase = configuredBase ? configuredBase.replace(/\/$/, '') : '';

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${apiBase}/api${path}`, {
      ...init,
      credentials: 'include',
      cache: 'no-store',
      headers: { 'Accept': 'application/json', ...(init?.body ? { 'Content-Type': 'application/json' } : {}), ...init?.headers },
    });
  } catch {
    throw new ApiError('Sunucuya erişilemiyor. Bağlantınızı ve servis durumunu kontrol edin.', 0);
  }

  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    if (response.status === 401 || response.status === 403) {
      throw new ApiError('Bu çalışma alanına erişim yetkiniz doğrulanamadı.', response.status);
    }
    throw new ApiError('Sunucu beklenen API yanıtını vermedi.', response.status);
  }
  const body: unknown = await response.json();
  if (!response.ok) {
    const data = body as { error?: string | { message?: string }; message?: string };
    const fromError = typeof data.error === 'string' ? data.error : data.error?.message;
    throw new ApiError(fromError || data.message || `İstek başarısız (${response.status}).`, response.status);
  }
  return body as T;
}

export const api = {
  me: () => request<{ actor: Actor; workspace: Workspace }>('/me'),
  login: (password: string) =>
    request<{ actor: Actor; workspace: Workspace }>('/auth/login', { method: 'POST', body: JSON.stringify({ password }) }),
  logout: () => request<{ ok: boolean }>('/auth/logout', { method: 'POST' }),
  resources: () => request<{ resources: Resource[] }>('/resources'),
  addResource: (input: Pick<Resource, 'name' | 'kind' | 'endpoint'>) =>
    request<{ resource: Resource }>('/resources', { method: 'POST', body: JSON.stringify(input) }),
  topology: (resourceId: string) => request<Topology>(`/topology?resourceId=${encodeURIComponent(resourceId)}`),
  diagnostics: (resourceId: string) => request<{ diagnostics: Diagnostic[] }>(`/diagnostics?resourceId=${encodeURIComponent(resourceId)}`),
  runDiagnostic: (resourceId: string, type: ResourceKind) =>
    request<{ diagnostic: Diagnostic }>('/diagnostics', { method: 'POST', body: JSON.stringify({ resourceId, type }) }),
  operations: (resourceId: string) => request<{ operations: Operation[] }>(`/operations?resourceId=${encodeURIComponent(resourceId)}`),
  operation: (operationId: string) => request<{ operation: Operation }>(`/operations/${encodeURIComponent(operationId)}`),
  repair: (resourceId: string, reason?: string) =>
    request<{ operation: Operation }>('/operations', { method: 'POST', body: JSON.stringify({ resourceId, action: 'connector.repair', reason }) }),
  audit: (resourceId: string) => request<{ events: AuditEvent[] }>(`/audit?resourceId=${encodeURIComponent(resourceId)}`),
  analyze: (resourceId: string) => request<{ analysis: Analysis }>('/analyze', { method: 'POST', body: JSON.stringify({ resourceId }) }),
};
